"use client";

/**
 * First-party client/install identity.
 *
 * Why this exists: anonymous identity used to ride ONLY on the
 * FingerprintJS OSS visitorId, which is a pure hash of device
 * characteristics - two strangers with the same popular phone model
 * (same iPhone + iOS + Safari version) collide BY DESIGN. In prod that
 * collapsed thousands of sessions onto a handful of "devices" and made
 * new users inherit strangers' spent free-quota buckets.
 *
 * The client ID is the opposite trade-off: a random UUID minted once
 * per browser install, persisted in localStorage and mirrored in a
 * cookie (either survives the other being cleared). It is trivially
 * resettable by the user (clear site data) - which is fine, because
 * the SERVER treats it as the primary identity and keeps the
 * fingerprint + IP buckets as anti-abuse backstops. See
 * backend/app/services/rate_limiter.py for the bucket math.
 *
 * Sent as the ``X-Client-Id`` header next to ``X-Fingerprint``.
 * Synchronous on purpose: unlike the fingerprint (async engine work),
 * identity must be available for the very first tracking POST of a
 * session - that first pageview carries the UTM/click-id attribution.
 */

const STORAGE_KEY = "menzil_cid";
const COOKIE_KEY = "menzil_cid";
const COOKIE_MAX_AGE_S = 60 * 60 * 24 * 730; // ~2 years

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

let cached: string | null = null;

function mintUuid(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // RFC-4122-shaped fallback from getRandomValues (old WebViews).
  try {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
    return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
  } catch {
    /* last resort below - non-crypto, but still per-install unique */
  }
  const rand = () => Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0");
  return `${rand()}${rand()}-${rand()}-4${rand().slice(1)}-a${rand().slice(1)}-${rand()}${rand()}${rand()}`;
}

function readCookie(): string | null {
  try {
    const m = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE_KEY}=([^;]+)`),
    );
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(v: string): void {
  try {
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(v)}; max-age=${COOKIE_MAX_AGE_S}; path=/; SameSite=Lax`;
  } catch {
    /* private mode / disabled cookies - localStorage still holds it */
  }
}

/**
 * The stable per-install client ID (lowercased UUID). Never throws;
 * returns "" during SSR. Re-heals whichever store lost the value.
 */
export function getClientId(): string {
  if (typeof window === "undefined") return "";
  if (cached) return cached;

  let fromStorage: string | null = null;
  try {
    fromStorage = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
  const fromCookie = readCookie();

  // Prefer localStorage; validate whatever we found so a corrupted
  // value can't become a permanent junk identity.
  let id = (fromStorage || fromCookie || "").toLowerCase();
  if (!UUID_RE.test(id)) id = mintUuid().toLowerCase();

  cached = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* cookie mirror below still persists it */
  }
  if (fromCookie !== id) writeCookie(id);
  return id;
}
